import React from 'react';
import usePageableProducts, { PRODUCTS_PAGE_SIZE } from '../../../hooks/usePageableProducts';
import './ProductsSearch.css';

function getScrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY || style.overflow;
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
}

export default function ProductsSearch({
  branchId = null,
  flatten = false,
  pageSize = PRODUCTS_PAGE_SIZE,
  categoryIds = null,
  productIds = null,
  enabled = true,
  placeholder = 'Поиск по названию',
  emptyText = 'Товары не найдены.',
  tone = 'page',
  className = '',
  toolbar = null,
  onProductsLoaded,
  children,
}) {
  const search = usePageableProducts({
    branchId,
    flatten,
    pageSize,
    categoryIds,
    productIds,
    enabled,
  });

  const {
    name,
    setName,
    page,
    products,
    setProducts,
    loading,
    loadingMore,
    ready,
    error,
    pagination,
    total,
    totalPages,
    hasMore,
    loadMore,
  } = search;

  const sentinelRef = React.useRef(null);
  const loadMoreRef = React.useRef(loadMore);
  loadMoreRef.current = loadMore;

  const onProductsLoadedRef = React.useRef(onProductsLoaded);
  onProductsLoadedRef.current = onProductsLoaded;

  React.useEffect(() => {
    if (!ready || loading) return;
    onProductsLoadedRef.current?.(products);
  }, [ready, loading, products]);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled || !hasMore || loading) return;

    const root = getScrollParent(sentinel);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        loadMoreRef.current();
      },
      { root, rootMargin: '160px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, hasMore, loading, products.length, ready]);

  const toolbarNode =
    typeof toolbar === 'function'
      ? toolbar({
          products,
          loading,
          loadingMore,
          ready,
          total,
          totalPages,
          page,
          error,
        })
      : toolbar;

  const body = children
    ? children({
        products,
        setProducts,
        loading,
        loadingMore,
        ready,
        error,
        pagination,
        total,
      })
    : null;

  const showEmpty = ready && !loading && !error && products.length === 0;

  return (
    <div className={`products-search${tone === 'sheet' ? ' products-search--sheet' : ''}${className ? ` ${className}` : ''}`}>
      <div className="products-search-bar">
        <div className="products-search-field">
          <svg
            className="products-search-icon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path
              d="M20 20l-3.2-3.2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            className="products-search-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            autoComplete="off"
            enterKeyHint="search"
          />
          {name ? (
            <button
              type="button"
              className="products-search-clear"
              onClick={() => setName('')}
              aria-label="Очистить поиск"
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {toolbarNode}

      <div className="products-search-body">
        {error ? (
          <div className="products-search-error">{error}</div>
        ) : showEmpty ? (
          <div className="products-search-empty">{emptyText}</div>
        ) : (
          body
        )}
      </div>

      {hasMore || loadingMore ? (
        <div className="products-search-more">
          <div ref={sentinelRef} className="products-search-sentinel" aria-hidden="true" />
          {loadingMore ? (
            <div className="products-search-loadingMore" aria-busy="true">
              <div className="products-search-loadingMoreBar" />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
